import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";

interface Organization {
  id: string;
  name: string;
  description: string | null;
  picture_url: string | null;
  theme_color: string | null;
  created_at: string;
  updated_at: string;
  user_roles: {
    role: 'owner' | 'admin' | 'member';
    position: number;
  }[];
}

export const useOrganizations = () => {
  const { user } = useAuth();

  return useQuery({
    queryKey: ["organizations", user?.id],
    queryFn: async (): Promise<Organization[]> => {
      if (!user?.id) {
        throw new Error("User not authenticated");
      }

      const { data, error } = await supabase
        .from("organizations")
        .select(`
          id,
          name,
          description,
          picture_url,
          theme_color,
          created_at,
          updated_at,
          user_roles!inner(role, position)
        `)
        .eq("user_roles.user_id", user.id);

      if (error) {
        throw error;
      }

      // Sort by position
      const sorted = (data || []).sort((a, b) => {
        const posA = a.user_roles[0]?.position ?? 0;
        const posB = b.user_roles[0]?.position ?? 0;
        return posA - posB;
      });

      return sorted;
    },
    enabled: !!user?.id,
  });
};